import { DropdownOption } from '@hs/ui-core-common';

import { Mode } from '../application-mode';
import { Client } from '../client';
import { DdvDate } from '../ddv-date';
import { DashboardFilter } from '../filters/dashboard-filter';
import { FilterQueryParams } from '../filters/filter-query-params';
import { Fund } from '../fund';
import { NamedQuery } from '../named-queries/named-query';
import { QueryPeriodTypeName } from '../named-queries/query-type';

export interface MinimumFilterParams {
    startDate?: string;
    endDate?: string;
    fundsCodes: string[];
    clientsCodes: string[];
}

// This should probably come out of dashboard so that there isn't
// a circular import path between dashboard and widget model
export interface DashboardPreference {
    funds?: Fund[];
    filters?: DashboardFilter[];
    startDate?: string;
    endDate?: string;
    activeDate?: string | null;
    dashboardPreferenceMode?: Mode;
    highlight?: string | null;
    areFiltersAppliedByMaster?: boolean;
    clients?: Client[];
    acknowledged?: boolean;
    includeManuallyReleased?: boolean;
    isPreferenceChangedOnRefresh?: boolean;
    additionalFilters?: Record<string, DropdownOption[]>[];
}

export function toDefaultQueryParams(
    fundOptions: Fund[],
    clientOptions: Client[],
    extraParametersForWorkspace: { widgets: { namedQuery?: NamedQuery }[] } | NamedQuery[] | undefined,
): DashboardPreference {
    let namedQueries: (NamedQuery | undefined)[];
    if (Array.isArray(extraParametersForWorkspace)) {
        namedQueries = extraParametersForWorkspace;
    } else {
        namedQueries = extraParametersForWorkspace?.widgets?.map((w) => w.namedQuery) ?? [];
    }

    const isCalendarVisible = !namedQueries.length || namedQueries.some((nq) => nq?.periodType !== QueryPeriodTypeName.NO_CALENDAR);
    const yesterdayDate: string = DdvDate.today.addDays(-1).toUSPaddedFormat();
    return {
        funds: fundOptions,
        startDate: isCalendarVisible ? yesterdayDate : '',
        endDate: isCalendarVisible ? yesterdayDate : '',
        filters: [],
        clients: clientOptions,
    };
}

export type QueryParamsBuilder = (
    fundOptions: Fund[],
    clientOptions: Client[],
    extraParametersForWorkspace: { widgets: { namedQuery?: NamedQuery }[] } | undefined,
    userPreferences?: MinimumFilterParams,
) => FilterQueryParams;

export function toPrioritizedFilterParams(
    fundOptions: Fund[],
    clientOptions: Client[],
    extraParametersForWorkspace: { widgets: { namedQuery?: NamedQuery }[] } | undefined,
    userPreferences?: MinimumFilterParams,
): FilterQueryParams {
    const filterParam = toDefaultQueryParams(fundOptions, clientOptions, extraParametersForWorkspace);
    if (userPreferences?.startDate && userPreferences?.endDate) {
        filterParam.startDate = userPreferences.startDate;
        filterParam.endDate = userPreferences.endDate;
    }

    filterParam.activeDate = filterParam.endDate;
    filterParam.funds = userPreferences?.fundsCodes.length ?
        (fundOptions?.filter((f) => userPreferences?.fundsCodes.includes(f.fundId)) || []) :
        filterParam.funds;
    filterParam.clients = userPreferences?.clientsCodes.length ?
        (clientOptions?.filter((c) => userPreferences?.clientsCodes.includes(c.clientId)) || []) :
        filterParam.clients;

    return filterParam;
}
